import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdBackspace, MdCall } from 'react-icons/md';
import { FaXmark } from 'react-icons/fa6';
import { AsYouType, isValidPhoneNumber } from 'libphonenumber-js';
import CustomModal from '../../util_components/custom_modal';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

// Auto-prefix `+` so the very first digit produces a valid E.164-style number.
// User types `1` → state becomes `+1`. We never store a leading non-`+` char.
const appendDigit = (n, d) => (n === '' ? `+${d}` : n + d);

// Strip everything except digits and a single leading `+` from pasted text.
// Inputs like "+1 (213) 555-0173", "tel:+12135550173", or "213-555-0173"
// all collapse to `+12135550173` / `+2135550173`.
const sanitizePastedNumber = (raw) => {
  if (!raw) return '';
  const cleaned = String(raw).replace(/[^\d+]/g, '');
  if (!cleaned) return '';
  const hadPlus = cleaned.startsWith('+');
  const digits = cleaned.replace(/\+/g, '');
  if (!digits) return hadPlus ? '+' : '';
  return `+${digits}`;
};

const KEY_BASE =
  'bg-transparent border-none rounded-[10px] inline-flex items-center justify-center ' +
  'cursor-pointer transition-all duration-75 ease-in-out hover:bg-gray-900/5 ' +
  'active:bg-gray-900/[0.12] active:scale-[0.96] disabled:cursor-not-allowed disabled:opacity-40';

const KEY_CLASSES = `${KEY_BASE} text-[22px] font-normal text-gray-900 h-14 tabular-nums select-none`;

const CALL_CLASSES = `${KEY_BASE} w-20 h-20`;

const CARD_STYLE = {
  width: 320,
  padding: 0,
  borderRadius: 24,
  overflow: 'hidden',
  background: 'linear-gradient(160deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.55) 100%)',
  backdropFilter: 'blur(28px) saturate(180%)',
  WebkitBackdropFilter: 'blur(28px) saturate(180%)',
  border: '1px solid rgba(255,255,255,0.5)',
  boxShadow:
    '0 30px 70px rgba(15,23,42,0.25), 0 1px 0 rgba(255,255,255,0.6) inset, 0 0 0 1px rgba(15,23,42,0.04)'
};

const OVERLAY_STYLE = {
  background:
    'radial-gradient(1200px 600px at 50% -10%, rgba(34,197,94,0.18), transparent 60%), rgba(15, 23, 42, 0.55)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)'
};

const DISPLAY_BG_STYLE = {
  background: 'linear-gradient(180deg, rgba(255,255,255,0.0) 0%, rgba(15,23,42,0.04) 100%)'
};

export default function DialerModal({ show, onClose, onCall }) {
  const [number, setNumber] = useState('');

  useEffect(() => {
    if (!show) setNumber('');
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    const onKeyDown = (e) => {
      // Don't hijack copy/paste shortcuts — let the paste handler below run
      // and let copy fall through to the browser's default for any text
      // selection inside the modal.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'v' || e.key === 'c')) return;
      if (e.key >= '0' && e.key <= '9') setNumber((n) => appendDigit(n, e.key));
      else if (e.key === '+' && number === '') setNumber('+');
      else if (e.key === 'Backspace') setNumber((n) => n.slice(0, -1));
      else if (e.key === 'Enter' && isValidPhoneNumber(number)) onCall?.(number);
    };
    const onPaste = (e) => {
      const text = (e.clipboardData || window.clipboardData)?.getData('text');
      const cleaned = sanitizePastedNumber(text);
      if (!cleaned) return;
      e.preventDefault();
      setNumber(cleaned);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('paste', onPaste);
    };
  }, [show, number, onCall]);

  const press = (k) => setNumber((n) => appendDigit(n, k));

  // Long-press (≥500ms) on backspace clears the entire number; tap deletes
  // one char. The longPressFiredRef flag swallows the click that follows a
  // long-press release so we don't also single-delete on top of the clear.
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);
  const cancelBackspaceLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };
  const startBackspaceLongPress = () => {
    cancelBackspaceLongPress();
    longPressFiredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      setNumber('');
      longPressTimerRef.current = null;
    }, 500);
  };
  const backspace = () => {
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    setNumber((n) => n.slice(0, -1));
  };

  useEffect(() => () => cancelBackspaceLongPress(), []);

  // canCall guards every entry point (button, Enter key, and placeCall) so a
  // partial input like "+", "+1", or a US-length-but-invalid string never
  // leaves the dialer.
  const canCall = useMemo(() => {
    if (!number) return false;
    try {
      return isValidPhoneNumber(number);
    } catch (e) {
      return false;
    }
  }, [number]);

  const placeCall = () => {
    if (!canCall) return;
    onCall?.(number);
  };

  // libphonenumber's AsYouType formats progressively (e.g. "+12134" → "+1 213 4")
  // and tolerates partial input — falls back to raw on parse errors.
  const displayNumber = useMemo(() => {
    if (!number) return '';
    try {
      return new AsYouType().input(number) || number;
    } catch (e) {
      return number;
    }
  }, [number]);

  return (
    <CustomModal
      show={show}
      close={onClose}
      showCloseBtn
      customClose
      outer_style={OVERLAY_STYLE}
      inner_style={CARD_STYLE}
    >
      <div className="flex justify-end px-2.5 pt-2.5">
        <div
          onClick={onClose}
          className="w-[30px] h-[30px] rounded-full border border-gray-900/15 bg-white/95 inline-flex items-center justify-center cursor-pointer shadow-sm transition-all duration-150 active:scale-95"
        >
          <FaXmark size={18} className="text-red-400 hover:text-red-800" />
        </div>
      </div>

      <div
        className="px-5 pt-3.5 pb-5 min-h-[86px] flex items-end justify-end border-b border-gray-900/5"
        style={DISPLAY_BG_STYLE}
      >
        {number ? (
          <div className="text-[22px] font-normal tracking-[0.3px] text-gray-900 tabular-nums text-right break-all leading-snug">
            {displayNumber}
          </div>
        ) : (
          <div className="text-sm font-medium text-gray-400 tracking-wide">Enter a number</div>
        )}
      </div>

      <div
        className="p-3 pb-4"
        style={{
          background:
            'linear-gradient(180deg, var(--surface-card) 0%, var(--surface-sunken) 60%, var(--border-default) 100%)'
        }}
      >
        <div className="grid grid-cols-3 gap-2">
          {KEYS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => press(k)}
              aria-label={`Dial ${k}`}
              className={KEY_CLASSES}
            >
              {k}
            </button>
          ))}
          <span />
          <button
            type="button"
            onClick={() => press('0')}
            aria-label="Dial 0"
            className={KEY_CLASSES}
          >
            0
          </button>
          <button
            type="button"
            onClick={backspace}
            onMouseDown={startBackspaceLongPress}
            onMouseUp={cancelBackspaceLongPress}
            onMouseLeave={cancelBackspaceLongPress}
            onTouchStart={startBackspaceLongPress}
            onTouchEnd={cancelBackspaceLongPress}
            onTouchCancel={cancelBackspaceLongPress}
            aria-label="Backspace (long-press to clear)"
            title="Long-press to clear"
            disabled={!number}
            className={KEY_CLASSES}
          >
            <MdBackspace size={22} className="text-gray-700" />
          </button>
        </div>

        <div className="flex justify-center mt-4">
          <button
            type="button"
            onClick={placeCall}
            disabled={!canCall}
            aria-label="Call"
            title={canCall ? 'Call' : 'Enter a valid phone number'}
            className={CALL_CLASSES}
          >
            <MdCall size={56} className={canCall ? 'text-green-600' : 'text-gray-400'} />
          </button>
        </div>
      </div>
    </CustomModal>
  );
}
